import { useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Box, FundingTrigger, Tooltip, withStyles } from 'vopay';
import { UtilButton, Title, Card, Button, BodyText, RadioCard } from 'UI';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import usePreFundingSettings from '@/hooks/useApi/usePreFundingSettings';
import useFetchDefaultOperationalBankAccount from '@/hooks/useApi/useFetchDefaultOperationalBankAccount';
import PreFundingSettingsSkeleton from './PreFundingSettingsSkeleton';
import styles from './PreFundingSettings.module.scss';

const cn = withStyles(styles);

export default function PreFundingSettings() {
  const history = useHistory();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.PreFundingSettings' });
  const { t: tSettings } = useTranslation('translations', { keyPrefix: 'Settings' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { permissions } = usePermissions();
  const {
    response: { Country },
  } = useClientData();
  const {
    fetching,
    updating,
    fundingTrigger,
    lastSavedTrigger,
    handleSelectTrigger,
    fetchPreFundingSettings,
    updatePreFundingSettings,
  } = usePreFundingSettings();
  const {
    fetching: fetchingBankAccount,
    operationalBankAccount,
    fetchDefaultOperationalBankAccount,
  } = useFetchDefaultOperationalBankAccount();
  const isLoading = fetching || fetchingBankAccount;

  useEffect(() => {
    async function loadPreFundingSettings() {
      try {
        await fetchPreFundingSettings();
      } catch {
        toast.neutral(t('fetchError', { defaultValue: 'Could not load pre-funding settings' }));
      }
    }

    async function loadDefaultBankAccount() {
      try {
        await fetchDefaultOperationalBankAccount({ Currency: Country === 'US' ? 'USD' : 'CAD' });
      } catch {
        toast.neutral(t('fetchBankAccountError', { defaultValue: 'Could not load your operational bank account' }));
      }
    }

    loadPreFundingSettings();
    loadDefaultBankAccount();
  }, [Country]);

  async function updateSettings() {
    try {
      await updatePreFundingSettings({ FundingTrigger: fundingTrigger });

      toast.success(t('saveSuccess', { defaultValue: 'Pre-funding settings saved' }));
    } catch {
      toast.neutral(t('saveError', { defaultValue: 'Could not save pre-funding settings' }));
    }
  }

  const triggerOptions: { value: FundingTrigger; title: string; description: string }[] = [
    {
      value: 'never',
      title: t('Triggers.Never.title', { defaultValue: 'Always from VoPay Balance' }),
      description: t('Triggers.Never.description', {
        defaultValue: 'Every payment is drawn from your VoPay balance.',
      }),
    },
    {
      value: 'insufficient-balance',
      title: t('Triggers.InsufficientBalance.title', {
        defaultValue: 'Fund from bank when the balance is short',
      }),
      description: t('Triggers.InsufficientBalance.description', {
        defaultValue:
          'When your balance does not cover a payment, the full payment amount is pulled from your default operational bank account, and the payment is sent once that funding settles.',
      }),
    },
    {
      value: 'always',
      title: t('Triggers.Always.title', { defaultValue: 'Always fund from bank account' }),
      description: t('Triggers.Always.description', {
        defaultValue:
          'Every payment is backed by a funding transaction for the full payment amount from your default operational bank account. Your VoPay balance is not used.',
      }),
    },
  ];

  return (
    <div className={cn('preFundingSettingsPage')}>
      <UtilButton
        iconLeft="chevron_left"
        size="large"
        onClick={() => {
          history.replace('/settings');
        }}
      >
        {tSettings('pageTitle', { defaultValue: 'Settings' })}
      </UtilButton>

      <Box flx="col" mt="md" mb="lg" gapSize="8px">
        <Title>{t('title', { defaultValue: 'Pre-Funding Settings' })}</Title>
        <Box>
          {t('description', {
            defaultValue:
              'Choose when payments are funded from your bank account. This setting applies to every payment created from your account.',
          })}
        </Box>
      </Box>

      <Card title={t('fundingBehaviourTitle', { defaultValue: 'Funding behaviour' })} style={{ padding: '20px 25px' }}>
        {isLoading ? (
          <PreFundingSettingsSkeleton />
        ) : (
          <Box flx="col" gapSize="12px">
            {triggerOptions.map((option) => (
              <RadioCard
                key={option.value}
                name="fundingTrigger"
                value={option.value}
                checked={fundingTrigger === option.value}
                onChange={() => handleSelectTrigger(option.value)}
                disabled={updating || (option.value !== 'never' && !operationalBankAccount)}
                indicatorPosition="left"
              >
                <Box flx="col" gapSize="4px">
                  <span className={cn('fontWeight600')}>{option.title}</span>
                  <span className={cn('description')}>{option.description}</span>
                </Box>
              </RadioCard>
            ))}

            {operationalBankAccount && (
              <>
                <Box mt="sm" ai="center" gapSize="8px">
                  <span className={cn('fontWeight600')}>{t('FundedFrom.label', { defaultValue: 'Funded from' })}</span>

                  <Tooltip
                    position="right"
                    icon="info"
                    information={
                      <BodyText>
                        <Box gapSize="8px" flx="col">
                          <span>
                            {t('FundedFrom.tooltip', {
                              defaultValue:
                                'Payments are funded from this account. To change the payment method it uses, go to the Operational Account page.',
                            })}
                          </span>
                          <Link to="/operational-accounts">
                            {t('FundedFrom.tooltipLink', { defaultValue: 'Operational Account' })}
                          </Link>
                        </Box>
                      </BodyText>
                    }
                  />
                </Box>

                <div className={cn('accountContainer')}>
                  {operationalBankAccount.InstitutionName} {operationalBankAccount.AccountNumber}
                </div>
              </>
            )}

            <div className={cn('description')}>
              <Trans
                t={t}
                i18nKey="footerNote"
                defaults="When a payment is funded from your bank account, the funding transaction is always for the full payment amount — not the difference. The payment stays in <bold>waiting</bold> until the funding settles."
                components={{ bold: <span className={cn('fontWeight600')} /> }}
              />
            </div>

            {permissions.AddSettings && (
              <Box jc="fe" mt="sm">
                <Button
                  colorScheme="primary"
                  disabled={updating || fundingTrigger === lastSavedTrigger}
                  onClick={updateSettings}
                >
                  {tGlobal('Buttons.save')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Card>
    </div>
  );
}
